import { useEffect, useState } from 'react';
import api from '../services/api';
import { MaintenanceWindow } from '../types';

export type MaintenanceStatusValue = 'none' | 'upcoming' | 'active';

export interface MaintenanceStatusSnapshot {
  status: MaintenanceStatusValue;
  window: MaintenanceWindow | null;
}

/**
 * Single shared maintenance-status poller.
 *
 * The app shell (maintenance lock in App.tsx) and the MaintenanceBanner both
 * need the same `/maintenance/status` answer. Instead of each running its own
 * interval, every consumer subscribes to this module-level store:
 *
 * - one request per POLL_INTERVAL_MS no matter how many consumers are mounted;
 * - polling pauses while the tab is hidden and refreshes when it becomes
 *   visible again;
 * - polling stops entirely when no consumer is subscribed.
 */
const POLL_INTERVAL_MS = 60_000;
// When a consumer mounts or the tab becomes visible, reuse a result that is
// at most this old instead of firing another request immediately.
const FRESH_ENOUGH_MS = 10_000;

let snapshot: MaintenanceStatusSnapshot | null = null;
let lastFetchAt = 0;
let inFlight: Promise<void> | null = null;
let subscriberCount = 0;
let intervalId: number | null = null;
const listeners = new Set<(next: MaintenanceStatusSnapshot | null) => void>();

function isHidden() {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function fetchStatus(): Promise<void> {
  if (inFlight) return inFlight;
  lastFetchAt = Date.now();
  const request = (async () => {
    try {
      const data = await api.getMaintenanceStatus();
      snapshot = { status: data.status, window: data.window };
      listeners.forEach((listener) => listener(snapshot));
    } catch {
      // Backend may be down — keep the last known status and don't crash.
    }
  })();
  inFlight = request;
  void request.finally(() => {
    if (inFlight === request) inFlight = null;
  });
  return request;
}

function refreshIfStale(maxAgeMs: number) {
  if (isHidden()) return;
  if (snapshot && Date.now() - lastFetchAt < maxAgeMs) return;
  void fetchStatus();
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') refreshIfStale(FRESH_ENOUGH_MS);
}

function startPolling() {
  if (intervalId !== null) return;
  intervalId = window.setInterval(() => {
    if (isHidden()) return;
    void fetchStatus();
  }, POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

function stopPolling() {
  if (intervalId !== null) window.clearInterval(intervalId);
  intervalId = null;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
}

/**
 * Subscribe to the shared maintenance status. Returns `null` until the first
 * successful response, or always when `enabled` is false (a disabled consumer
 * does not keep the poller alive).
 */
export function useMaintenanceStatus(enabled = true): MaintenanceStatusSnapshot | null {
  const [current, setCurrent] = useState<MaintenanceStatusSnapshot | null>(() => (enabled ? snapshot : null));

  useEffect(() => {
    if (!enabled) {
      setCurrent(null);
      return;
    }

    setCurrent(snapshot);
    listeners.add(setCurrent);
    subscriberCount += 1;
    if (subscriberCount === 1) startPolling();
    refreshIfStale(FRESH_ENOUGH_MS);

    return () => {
      listeners.delete(setCurrent);
      subscriberCount = Math.max(0, subscriberCount - 1);
      if (subscriberCount === 0) stopPolling();
    };
  }, [enabled]);

  return enabled ? current : null;
}
